import React, { useEffect, useMemo } from 'react';

//redux
import { useSelector, useDispatch } from 'react-redux';

import { createSelector } from 'reselect';
import { withTranslation } from 'react-i18next';
import { getExportations } from '../../slices/exportations/thunk';
import { formatBrazilDate } from '../../helpers/format';
import TableContainer from '../../Components/Common/TableContainer';
import BreadCrumb from '../../Components/Common/BreadCrumb';
import useQsParams from '../../Components/Hooks/QueryString';
import { Container, Row, Col, Card, CardBody } from 'reactstrap';

const Exportations = (props) => {
  const dispatch = useDispatch();
  const { searchParams, setSearchParams } = useQsParams();

  const exportationsState = createSelector(
    (state) => state.Exportations.exportations,
    (exportations) => exportations || []
  );

  const loadingState = createSelector(
    (state) => state.Exportations.isLoading,
    (isLoading) => isLoading || false
  );

  const metaState = createSelector(
    (state) => state.Exportations.meta,
    (meta) => meta || {}
  );

  const exportations = useSelector(exportationsState);
  const isLoading = useSelector(loadingState);
  const meta = useSelector(metaState);

  useEffect(() => {
    getData();
  }, [dispatch, searchParams.toString()]);

  const getData = () => {
    const params = { page: searchParams.get('page') ?? 1 };
    dispatch(getExportations(params));
  };

  const { t } = props;

  const getStatusBadge = (status) => {
    const statusMap = {
      Pending: 'bg-warning-subtle text-warning',
      Processing: 'bg-info-subtle text-info',
      Completed: 'bg-success-subtle text-success',
      Failed: 'bg-danger-subtle text-danger',
    };
    return statusMap[status] || 'bg-secondary-subtle text-secondary';
  };

  const handleDownload = (fileUrl) => {
    if (fileUrl) {
      window.open(fileUrl, '_blank');
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: t('exportations.table.description'),
        accessor: 'description',
        Cell: (cellProps) => cellProps.row.original.description,
      },
      {
        Header: t('exportations.table.status'),
        accessor: 'status',
        Cell: (cellProps) => {
          const status = cellProps.row.original.status;
          return (
            <span className={`badge rounded-pill ${getStatusBadge(status)}`}>
              {t(`exportations.status.${status.toLowerCase()}`)}
            </span>
          );
        },
      },
      {
        Header: t('exportations.table.requested-at'),
        accessor: 'requestedAt',
        Cell: (cellProps) => formatBrazilDate(cellProps.row.original.requestedAt),
      },
      {
        Header: t('exportations.table.records'),
        accessor: 'totalRecords',
        Cell: (cellProps) => cellProps.row.original.totalRecords?.toLocaleString('pt-BR') || '-',
      },
      {
        Header: t('exportations.table.actions'),
        Cell: (cellProps) => {
          const { status, fileUrl } = cellProps.row.original;
          return (
            <div className="d-flex gap-2">
              {status === 'Completed' && fileUrl && (
                <button
                  className="btn btn-sm btn-primary"
                  onClick={() => handleDownload(fileUrl)}
                >
                  <i className="ri-download-line me-1" />
                  {t('exportations.actions.download')}
                </button>
              )}
            </div>
          );
        },
      },
    ],
    [t],
  );

  document.title = 'Exportações | Opencashback';

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title={t('exportations.title')}
            pageTitle={t('exportations.page-title')}
          />
          <Row>
            <Col lg="12">
              <Card>
                <CardBody>
                  <TableContainer
                    columns={columns}
                    data={exportations || []}
                    isLoading={isLoading}
                    activePage={meta?.page || 1}
                    pageSize={meta?.pageSize || 10}
                    totalPages={meta?.totalPages || 1}
                    setPage={(p) => setSearchParams('page', p)}
                    count={meta?.total || 0}
                    t={t}
                    theadClass="table-light"
                    divClass="table-responsive table-card mb-0"
                    tableClass="align-middle table-nowrap"
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>

        </Container>
      </div>
    </React.Fragment>
  );
};

export default withTranslation()(Exportations);